/**
 * What the screen shows, decided here. Pure functions, unit-tested, no React.
 *
 * Three rules hold this file together, the same three that hold `ui/views.py`:
 *
 * - Nothing leaves here as a raw identifier; everything goes through `labels.ts`.
 * - The screen never asks a question the data can answer: `nextAction` decides what the one
 *   primary button says, so nobody is asked which pipeline step to resume from.
 * - What needs review is computed, not left to the reader: `attentionItems` gathers failed
 *   controls, conflicts, low confidence and declared gaps into one ranked list.
 */

import type {
  AssessmentOut,
  CaseOut,
  EnrichmentFinding,
  EvidenceOut,
  ProfileField,
  ProfileOut,
  ReportOut,
  RunDiff,
  RunOut,
  RunStepOut,
} from "../api/types";
import type { Metrics, RiskScoreOut } from "../api/types";
import {
  CASE_STATUS,
  DECISIONS,
  DECISION_MATCHES,
  DECISION_MEANING,
  CATEGORIES,
  EVIDENCE_KINDS,
  FIELD_SECTIONS,
  PRIMARY_REPORT_FORMAT,
  RATIO_ORDER,
  RECOMMENDATION_MEANING,
  RECOMMENDATIONS,
  REPORT_FORMATS,
  RUN_STATUS,
  SEVERITIES,
  SOURCES,
  STEP_LABELS,
  STEP_ORDER,
  STEP_RUNNING,
  VALIDATION_SEVERITY,
  fieldLabel,
  label,
} from "./labels";

export type Tone = "good" | "warn" | "bad" | "neutral";

/** Below this, a figure is shown as needing a second look rather than as a fact. */
export const LOW_CONFIDENCE = 0.7;

const RECOMMENDATION_TONE: Record<string, Tone> = {
  favorable: "good",
  favorable_with_conditions: "warn",
  unfavorable: "bad",
  insufficient_information: "neutral",
};
const CASE_STATUS_TONE: Record<string, Tone> = {
  draft: "neutral",
  queued: "neutral",
  running: "warn",
  done: "good",
  failed: "bad",
};
export const CATEGORY_TONE: Record<string, Tone> = {
  red_flag: "bad",
  risk: "warn",
  strength: "good",
  observation: "neutral",
};
const SEVERITY_TONE: Record<string, Tone> = {
  error: "bad",
  warning: "warn",
  info: "neutral",
};

// --- numbers -------------------------------------------------------------------------------

/** es-AR digits: thousands with a dot, decimals with a comma. */
export function formatNumber(value: number, decimals = 0): string {
  return value.toLocaleString("es-AR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * One field as the analyst should read it, unit included.
 *
 * Ratios are stored as fractions and read as percentages — a margin of 0,2619 is "26,19 %", the
 * way it appears in a credit report — so the screen never shows a number the analyst has to
 * convert in their head.
 */
export function formatValue(field: ProfileField | null | undefined): string {
  if (!field || field.value === null || field.value === undefined) return "—";
  const { value, unit } = field;

  if (typeof value === "boolean") return value ? "Sí" : "No";
  if (typeof value === "number") {
    if (unit === "pct") return `${formatNumber(value * 100, 2)} %`;
    if (unit === "ARS") return `$ ${formatNumber(value)}`;
    if (unit === "days") return `${formatNumber(value, 0)} días`;
    if (unit === "count" || unit === "level" || Number.isInteger(value)) return formatNumber(value);
    return formatNumber(value, 2);
  }
  if (Array.isArray(value)) {
    return value.length === 1 ? String(value[0]).slice(0, 80) : `${value.length} ítem(s)`;
  }
  if (typeof value === "object") return `${Object.keys(value).length} entrada(s)`;
  return String(value);
}

/** (text, tone). No confidence recorded is not the same as low confidence. */
export function confidenceLabel(confidence: number | null): {
  text: string;
  tone: Tone;
} {
  if (confidence === null || confidence === undefined) return { text: "—", tone: "neutral" };
  if (confidence >= 0.9) return { text: "Alta", tone: "good" };
  if (confidence >= LOW_CONFIDENCE) return { text: "Media", tone: "warn" };
  return { text: "Baja", tone: "bad" };
}

// --- state of a legajo ---------------------------------------------------------------------

export interface Chip {
  text: string;
  tone: Tone;
}

/**
 * The one-word answer to "where is this legajo".
 *
 * Taken from the run rather than from `case.status` whenever a run exists: the case row says
 * `queued` from the moment a job is created, which would leave a legajo reading "En cola" while
 * the worker is visibly on step five.
 */
export function caseState(kase: CaseOut): Chip {
  const run = kase.latest_run;
  if (run) {
    if (run.status === "running") {
      const step = label(STEP_LABELS, run.current_step, "");
      return {
        text: step ? `Analizando · ${step}` : "Analizando",
        tone: "warn",
      };
    }
    if (run.status === "failed") return { text: "Interrumpido", tone: "bad" };
    if (run.status === "pending") return { text: "En cola", tone: "neutral" };
  }
  const status = kase.status ?? "draft";
  return {
    text: label(CASE_STATUS, status),
    tone: CASE_STATUS_TONE[status] ?? "neutral",
  };
}

export function caseRisk(kase: CaseOut): Chip | null {
  if (!kase.recommendation) return null;
  return {
    text: label(RECOMMENDATIONS, kase.recommendation),
    tone: RECOMMENDATION_TONE[kase.recommendation] ?? "neutral",
  };
}

// Sort order for the queue: what needs the analyst comes first. An interrupted legajo is the one
// nobody else will notice; a finished unfavourable one is a decision waiting to be made.
const QUEUE_RANK: Record<string, number> = {
  failed: 0,
  unfavorable: 1,
  insufficient_information: 2,
  favorable_with_conditions: 3,
  running: 4,
  favorable: 5,
};

export function queueRank(kase: CaseOut): number {
  const status = kase.latest_run?.status;
  if (status === "failed") return QUEUE_RANK.failed!;
  if (status === "running") return QUEUE_RANK.running!;
  const recommendation = kase.recommendation;
  if (recommendation && recommendation in QUEUE_RANK) return QUEUE_RANK[recommendation]!;
  return 6;
}

export interface QueueRow {
  id: string;
  legalName: string;
  cuit: string;
  state: Chip;
  risk: Chip | null;
  grade: Chip | null;
  gradeIsPartial: boolean;
  decision: Chip | null;
  assignedTo: string | null;
  updatedAt: string | null;
  redFlags: number;
  gaps: number;
  documents: number;
  needsAttention: boolean;
  running: boolean;
  analysed: boolean;
  decided: boolean;
}

export function queueRows(cases: CaseOut[]): QueueRow[] {
  return [...cases]
    .sort((a, b) => queueRank(a) - queueRank(b) || a.legal_name.localeCompare(b.legal_name))
    .map((kase) => {
      const state = caseState(kase);
      const risk = caseRisk(kase);
      const interrupted = state.tone === "bad";
      return {
        id: kase.id,
        legalName: kase.legal_name,
        cuit: kase.cuit,
        state,
        risk,
        grade: gradeChip(kase.score),
        gradeIsPartial: !!kase.score?.partial,
        decision: decisionChip(kase.decision),
        assignedTo: kase.assigned_to_name ?? null,
        updatedAt: kase.updated_at ?? null,
        redFlags: kase.red_flags ?? 0,
        gaps: kase.open_gaps ?? 0,
        documents: kase.documents?.length ?? 0,
        // A decided legajo is finished work, however alarming its findings were.
        needsAttention: !kase.decision && (interrupted || !!kase.red_flags || !!kase.open_gaps),
        running: kase.latest_run?.status === "running",
        analysed: !!risk,
        decided: !!kase.decision,
      };
    });
}

/** Worst grade first; a legajo without a grade sorts last, since it says nothing either way. */
export function byGradeDescending(rows: QueueRow[]): QueueRow[] {
  const rank = (row: QueueRow) =>
    row.grade ? ["E", "D", "C", "B", "A"].indexOf(row.grade.text) : 99;
  return [...rows].sort((a, b) => rank(a) - rank(b) || a.legalName.localeCompare(b.legalName));
}

/**
 * A qué fila lleva una tecla en la cola.
 *
 * La cola es una tabla, y una tabla que sólo se recorre con Tab obliga a pasar por cada celda para
 * bajar una fila. Con flechas se recorre como se la lee. Devuelve `null` cuando la tecla no es de
 * navegación, para que el resto siga su camino — escribir en el buscador no puede moverle el foco
 * a nadie.
 */
export function queueKey(key: string, current: number, count: number): number | null {
  if (count === 0) return null;
  const clamp = (n: number) => Math.max(0, Math.min(count - 1, n));
  if (key === "ArrowDown") return clamp(current + 1);
  if (key === "ArrowUp") return clamp(current - 1);
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  return null;
}

export const QUEUE_VIEWS = [
  "Todos",
  "Requieren atención",
  "Pendientes de decisión",
  "En análisis",
  "Sin asignar",
  "Decididos",
] as const;
export type QueueView = (typeof QUEUE_VIEWS)[number];

/**
 * Cada vista de la cola, como el parámetro que entiende el servidor.
 *
 * Antes el filtro pasaba acá, sobre las filas que ya habían llegado: con una cartera de miles,
 * «pendientes de decisión» mostraba los pendientes *de esa página*, que no es lo que dice la
 * etiqueta. Ahora la vista viaja y la base decide sobre la cartera entera; de este lado queda sólo
 * la traducción, que es lo único que esta capa sabe mejor que el servidor: cómo se llama cada cosa
 * en la pantalla.
 */
/**
 * Quién puede tocar los umbrales del banco.
 *
 * La API lo exige igual —esconder el enlace es cortesía, no seguridad—, pero la regla vive acá
 * porque la pantalla la necesita en dos lugares: el riel, para no ofrecer una sección que va a
 * rebotar, y la pantalla misma. Escrita dos veces, se corrige una sola.
 */
export function canEditPolicy(role: string | undefined | null): boolean {
  return role === "referent" || role === "admin";
}

export const VIEW_PARAM: Record<QueueView, string | null> = {
  Todos: null,
  "Requieren atención": "atencion",
  "Pendientes de decisión": "pendientes",
  "En análisis": "corriendo",
  "Sin asignar": "sin_asignar",
  Decididos: "decididos",
};

export function viewParam(view: QueueView): string | null {
  return VIEW_PARAM[view] ?? null;
}

/** Name or CUIT, accent- and separator-insensitive: analysts type `30123456789`. */
export function fold(text: string): string {
  return (text ?? "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[-. ]/g, "");
}

export function searchCases(cases: CaseOut[], query: string): CaseOut[] {
  const needle = fold(query);
  if (!needle) return cases;
  return cases.filter((c) => fold(c.legal_name).includes(needle) || fold(c.cuit).includes(needle));
}

export function queueStats(cases: CaseOut[]) {
  const rows = queueRows(cases);
  return {
    Legajos: cases.length,
    "En análisis": rows.filter((r) => r.running).length,
    "Requieren atención": cases.filter((c) => queueRank(c) <= 2).length,
    Analizados: rows.filter((r) => r.analysed).length,
  };
}

// --- the one primary action ------------------------------------------------------------------

export interface Action {
  key: "upload" | "analyze" | "waiting" | "retry" | "report";
  label: string;
  hint: string;
  fromStep: string | null;
  enabled: boolean;
}

/**
 * The next thing to do with this legajo — decided here, never asked of the analyst.
 *
 * The Streamlit screen this replaces offered a "resume from step" dropdown listing seven internal
 * step names. An analyst cannot know whether to resume from `consolidate` or `validate`, and
 * should not have to: a run that failed resumes from the step that failed.
 */
export function nextAction(kase: CaseOut): Action {
  const run = kase.latest_run;
  if (!kase.documents?.length) {
    return {
      key: "upload",
      label: "Subir documentos",
      hint: "Un legajo necesita al menos un documento.",
      fromStep: null,
      enabled: true,
    };
  }
  if (run && (run.status === "running" || run.status === "pending")) {
    return {
      key: "waiting",
      label: "Analizando…",
      hint: label(STEP_RUNNING, run.current_step, "Procesando…"),
      fromStep: null,
      enabled: false,
    };
  }
  if (run && run.status === "failed") {
    const where = label(STEP_LABELS, run.current_step, "");
    return {
      key: "retry",
      label: "Reintentar análisis",
      hint: where ? `Se reanuda desde «${where}» y se conserva lo ya procesado.` : "",
      fromStep: run.current_step,
      enabled: true,
    };
  }
  if (kase.recommendation) {
    return {
      key: "report",
      label: "Descargar informe",
      hint: "Borrador para revisión del analista.",
      fromStep: null,
      enabled: true,
    };
  }
  return {
    key: "analyze",
    label: "Iniciar análisis",
    hint: "Toma unos minutos.",
    fromStep: null,
    enabled: true,
  };
}

export function isRunning(kase: CaseOut | undefined | null): boolean {
  const status = kase?.latest_run?.status;
  return status === "running" || status === "pending";
}

// --- progress ------------------------------------------------------------------------------

export function runProgress(kase: CaseOut) {
  const run = kase.latest_run;
  if (!run) return null;
  const total = run.steps_total || STEP_ORDER.length;
  const done = run.steps_done ?? 0;
  return {
    done,
    total,
    current: label(STEP_RUNNING, run.current_step, "Preparando…"),
    fraction: total ? Math.min(done / total, 1) : 0,
  };
}

/**
 * The latest attempt of each step.
 *
 * Retrying a run appends a second set of rows to the same run, so a step can appear `done` from
 * the first pass and `failed` from the retry. Steps arrive in creation order, so the last row for
 * a name is what is true now; showing the first told the analyst a step had succeeded when its
 * retry had just failed.
 */
export function latestSteps(run: RunOut): Record<string, RunStepOut> {
  const out: Record<string, RunStepOut> = {};
  for (const step of run.steps ?? []) if (!step.document_id) out[step.step] = step;
  return out;
}

export function stepTimeline(run: RunOut) {
  const byStep = latestSteps(run);
  return STEP_ORDER.map((name) => {
    const step = byStep[name];
    const status = step?.status ?? "pending";
    return {
      step: name,
      // A resumed run marks the steps it did not redo; that is done, not pending.
      label: STEP_LABELS[name] + (status === "skipped" ? " · reutilizado" : ""),
      status,
      error: step?.error ?? null,
    };
  });
}

function duration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${formatNumber(seconds, 1)} s`;
  return `${Math.floor(seconds / 60)} min ${Math.floor(seconds % 60)} s`;
}

/** The technical trace. A retried run keeps every attempt, so repeated steps are numbered. */
export function traceRows(run: RunOut) {
  const counts = new Map<string, number>();
  const totals = new Map<string, number>();
  for (const step of run.steps ?? []) {
    const key = `${step.step}|${step.document_id ?? ""}`;
    totals.set(key, (totals.get(key) ?? 0) + 1);
  }
  const repeated = new Set(
    [...totals.entries()].filter(([, n]) => n > 1).map(([key]) => key.split("|")[0]!),
  );

  return (run.steps ?? []).map((step) => {
    const key = `${step.step}|${step.document_id ?? ""}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
    return {
      name: step.document_id ? `↳ ${step.document ?? "documento"}` : label(STEP_LABELS, step.step),
      attempt: repeated.has(step.step) ? String(counts.get(key)) : "",
      status: label(RUN_STATUS, step.status),
      tone: (step.status === "failed" ? "bad"
      : step.status === "done" ? "good"
      : "neutral") as Tone,
      model: step.model ?? "—",
      tokens: `${formatNumber(step.input_tokens ?? 0)} / ${formatNumber(step.output_tokens ?? 0)}`,
      cost: (Number(step.cost_usd) || 0).toFixed(4),
      duration: duration(step.latency_ms ?? 0),
      error: (step.error ?? "").slice(0, 200),
    };
  });
}

export function runTotals(run: RunOut) {
  return [
    ["Estado", label(RUN_STATUS, run.status)],
    ["Tokens de entrada", formatNumber(run.total_input_tokens ?? 0)],
    ["Tokens de salida", formatNumber(run.total_output_tokens ?? 0)],
    ["Costo", `USD ${(Number(run.total_cost_usd) || 0).toFixed(4)}`],
  ] as [string, string][];
}

// --- the figures ---------------------------------------------------------------------------

export interface FigureRow {
  name: string;
  label: string;
  values: Record<string, string>;
  confidence: { text: string; tone: Tone };
  conflict: boolean;
  evidenceId: string | null;
}

export interface FigureSection {
  name: string;
  periods: string[];
  rows: FigureRow[];
}

/** Extracted figures grouped the way a credit file is read, one column per fiscal year. */
/**
 * Qué cambió entre dos corridas, como lo lee el analista.
 *
 * La distinción que hace el back — «cambió» contra «apareció» — se sostiene acá: una cifra que
 * antes no estaba se muestra sin un «antes» inventado, porque presentar «de — a 5.200» leería como
 * si la empresa se hubiera movido y lo que se movió es lo que sabemos de ella.
 */
export function diffRows(diff: RunDiff) {
  return diff.fields.map((f) => ({
    key: `${f.name}:${f.period ?? ""}`,
    label: fieldLabel(f.name),
    period: f.period ?? "—",
    before: diffValue(f.before),
    after: diffValue(f.after),
    kind: f.kind,
    tone: (f.kind === "cambió" ? "warn"
    : f.kind === "apareció" ? "good"
    : "bad") as Tone,
  }));
}

function diffValue(value: number | string | null): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number")
    return Number.isInteger(value) ? formatNumber(value) : formatNumber(value, 2);
  return String(value);
}

/** Lo primero que hay que ver: si la calificación se movió, y para dónde. */
export function diffHeadline(diff: RunDiff): { text: string; tone: Tone } | null {
  const { grade_before: before, grade_after: after } = diff;
  if (!before && !after) return null;
  if (before === after) return { text: `La calificación se mantiene en ${after}`, tone: "neutral" };
  if (!before) return { text: `Ahora califica ${after}`, tone: "neutral" };
  if (!after) return { text: `Ya no hay calificación (antes ${before})`, tone: "warn" };
  // El orden de las letras es el orden del riesgo: A es mejor que E.
  const improved = after < before;
  return {
    text: `La calificación pasó de ${before} a ${after}`,
    tone: improved ? "good" : "bad",
  };
}

export function figureSections(profile: ProfileOut): FigureSection[] {
  const byName = new Map<string, ProfileField[]>();
  for (const field of profile.fields ?? []) {
    if (field.is_derived) continue;
    const list = byName.get(field.name) ?? [];
    list.push(field);
    byName.set(field.name, list);
  }

  const sections: FigureSection[] = [];
  for (const { name, fields } of FIELD_SECTIONS) {
    const present = fields.filter((f) => byName.has(f));
    if (!present.length) continue;

    const periods = [
      ...new Set(
        present.flatMap((f) =>
          byName
            .get(f)!
            .map((e) => e.period)
            .filter(Boolean),
        ),
      ),
    ].sort((a, b) => String(b).localeCompare(String(a))) as string[];

    const rows: FigureRow[] = present.map((fieldName) => {
      const entries = byName.get(fieldName)!;
      const values: Record<string, string> = {};
      if (periods.length) {
        for (const period of periods) {
          values[period] = formatValue(entries.find((e) => e.period === period) ?? null);
        }
        if (!entries.some((e) => e.period)) values[periods[0]!] = formatValue(entries[0]);
      } else {
        values["Valor"] = formatValue(entries[0]);
      }
      const best = entries.reduce((a, b) => ((a.confidence ?? 0) >= (b.confidence ?? 0) ? a : b));
      return {
        name: fieldName,
        label: fieldLabel(fieldName),
        values,
        confidence: confidenceLabel(best.confidence),
        conflict: entries.some((e) => e.conflict),
        evidenceId: entries.find((e) => e.evidence_id)?.evidence_id ?? null,
      };
    });

    sections.push({
      name,
      periods: periods.length ? periods : ["Valor"],
      rows,
    });
  }
  return sections;
}

export function ratioRows(profile: ProfileOut) {
  const derived = (profile.fields ?? []).filter((f) => f.is_derived);
  const periods = [...new Set(derived.map((f) => f.period).filter(Boolean))].sort((a, b) =>
    String(b).localeCompare(String(a)),
  ) as string[];
  const present = new Set(derived.map((f) => f.name));
  const ordered = [
    ...RATIO_ORDER.filter((n) => present.has(n)),
    ...[...present].filter((n) => !RATIO_ORDER.includes(n)).sort(),
  ];
  const rows = ordered.map((name) => ({
    label: fieldLabel(name),
    values: Object.fromEntries(
      periods.map((p) => [p, formatValue(derived.find((f) => f.name === name && f.period === p))]),
    ),
  }));
  return { periods, rows };
}

/** Failures first: an analyst opening this is looking for what did not check out. */
export function validationRows(profile: ProfileOut) {
  return [...(profile.validations ?? [])]
    .sort(
      (a, b) =>
        Number(a.passed) - Number(b.passed) ||
        Number(a.severity !== "error") - Number(b.severity !== "error"),
    )
    .map((v) => ({
      passed: v.passed,
      severity: label(VALIDATION_SEVERITY, v.severity),
      tone: SEVERITY_TONE[v.severity] ?? "neutral",
      message: v.message,
      fields: (v.field_names ?? []).map(fieldLabel).join(", "),
    }));
}

export function conflictRows(profile: ProfileOut) {
  return (profile.conflicts ?? []).map((f) => ({
    label: fieldLabel(f.name),
    period: f.period ?? "—",
    value: formatValue(f),
    resolution: f.resolution ?? "",
  }));
}

/**
 * The pipeline converts amounts to pesos and takes the magnitude of outflows. An analyst reading
 * "$ 240.000.000" beside a quote that says "Ventas netas 240.000" will assume the system is
 * wrong, so wherever a figure was changed the screen says what was changed and why.
 */
const NORMALISATION_NOTES: Record<string, string> = {
  unit: "El documento informa este importe en otra escala (miles o millones); el sistema lo convirtió a pesos.",
  sign: "El documento imprime este importe con signo negativo (entre paréntesis); el perfil guarda su magnitud.",
};

export function normalisationNote(
  profile: ProfileOut | null | undefined,
  name: string,
  period: string | null,
): string | null {
  for (const entry of profile?.extra?.normalisations ?? []) {
    if (entry.field !== name) continue;
    if (period !== null && entry.period !== null && entry.period !== period) continue;
    const note = NORMALISATION_NOTES[entry.kind];
    if (note) return note;
  }
  return null;
}

// --- what needs a human ----------------------------------------------------------------------

export interface AttentionItem {
  tone: Tone;
  kind: string;
  text: string;
}

/**
 * Everything the analyst should look at before signing, ranked.
 *
 * Four sources that used to live in four different tabs: rules that did not pass, documents that
 * disagree, figures the extractor was unsure about, and information the analysis says is missing.
 */
export function attentionItems(
  profile: ProfileOut | null | undefined,
  assessment: AssessmentOut | null | undefined,
): AttentionItem[] {
  const items: AttentionItem[] = [];

  for (const validation of profile?.validations ?? []) {
    if (validation.passed) continue;
    items.push({
      tone: SEVERITY_TONE[validation.severity] ?? "warn",
      kind: "Control",
      text: validation.message,
    });
  }

  for (const field of profile?.conflicts ?? []) {
    items.push({
      tone: "warn",
      kind: "Conflicto",
      text: `${fieldLabel(field.name)}${field.period ? ` · ${field.period}` : ""}: los documentos no coinciden. Se adoptó ${formatValue(field)}.`,
    });
  }

  for (const field of profile?.fields ?? []) {
    if (field.confidence === null || field.confidence >= LOW_CONFIDENCE) continue;
    items.push({
      tone: "warn",
      kind: "Baja confianza",
      text: `${fieldLabel(field.name)}: el sistema no está seguro de este valor. Verificar.`,
    });
  }

  for (const gap of assessment?.information_gaps ?? []) {
    items.push({ tone: "neutral", kind: "Falta información", text: gap });
  }

  const order: Record<Tone, number> = { bad: 0, warn: 1, neutral: 2, good: 3 };
  return items.sort((a, b) => order[a.tone] - order[b.tone]);
}

// --- external sources ------------------------------------------------------------------------

/**
 * External findings grouped by where they came from. The distinction between the legajo and the
 * outside world is the one an analyst must never lose: a figure from audited statements and a
 * claim from a news site do not carry the same weight.
 */
export function enrichmentGroups(findings: EnrichmentFinding[]) {
  const grouped = new Map<string, EnrichmentFinding[]>();
  for (const finding of findings) {
    const list = grouped.get(finding.source) ?? [];
    list.push(finding);
    grouped.set(finding.source, list);
  }
  return ["bcra_api", "web"]
    .filter((source) => grouped.has(source))
    .map((source) => ({
      source: label(SOURCES, source),
      findings: grouped.get(source)!.sort((a, b) => (b.relevance ?? 0) - (a.relevance ?? 0)),
    }));
}

// --- the verdict ----------------------------------------------------------------------------

export function verdict(assessment: AssessmentOut) {
  const findings = assessment.findings ?? [];
  const count = (category: string) => findings.filter((f) => f.category === category).length;
  return {
    label: label(RECOMMENDATIONS, assessment.recommendation),
    meaning: RECOMMENDATION_MEANING[assessment.recommendation] ?? "",
    tone: RECOMMENDATION_TONE[assessment.recommendation] ?? "neutral",
    redFlags: count("red_flag"),
    risks: count("risk"),
    strengths: count("strength"),
    gaps: (assessment.information_gaps ?? []).length,
    conditions: (assessment.conditions ?? []).length,
  };
}

/** Red flags first: what a credit analyst scans for. */
export function findingsByCategory(assessment: AssessmentOut) {
  const order = ["red_flag", "risk", "strength", "observation"];
  const grouped = new Map<string, AssessmentOut["findings"]>();
  for (const finding of assessment.findings ?? []) {
    const list = grouped.get(finding.category) ?? [];
    list.push(finding);
    grouped.set(finding.category, list);
  }
  const extras = [...grouped.keys()].filter((k) => !order.includes(k)).sort();
  return [...order, ...extras]
    .filter((category) => grouped.has(category))
    .map((category) => ({
      label: label(CATEGORIES, category),
      tone: CATEGORY_TONE[category] ?? "neutral",
      findings: grouped.get(category)!,
    }));
}

export function severityLabel(severity: string | null): string {
  return severity ? (SEVERITIES[severity] ?? severity) : "";
}

// --- the credit score ---------------------------------------------------------------------------

/**
 * The grade, coloured by what it means for the credit.
 *
 * A grade shown without its coverage is a half-truth: 62 computed on a third of the scorecard is a
 * different claim from 62 computed on all of it. Every place that shows the grade shows both.
 */
export function gradeChip(
  score: { grade: string | null; partial: boolean } | null | undefined,
): Chip | null {
  if (!score?.grade) return null;
  const tone: Tone =
    score.partial ? "neutral"
    : score.grade === "A" || score.grade === "B" ? "good"
    : score.grade === "C" ? "warn"
    : "bad";
  return { text: score.grade, tone };
}

export function coverageLabel(coverage: number, partial: boolean): string {
  const share = `${Math.round(coverage * 100)}% del scorecard`;
  return partial ? `${share} — parcial` : share;
}

/** A component's contribution, for the bar that shows where the points went. */
export function componentRows(score: RiskScoreOut) {
  return (score.payload.components ?? []).map((c) => ({
    key: c.key,
    label: c.label,
    question: c.question,
    weight: c.weight,
    score: c.score,
    coverage: c.coverage,
    tone: (c.score === null ? "neutral"
    : c.score >= 80 ? "good"
    : c.score >= 50 ? "warn"
    : "bad") as Tone,
    indicators: c.indicators,
  }));
}

/** How an indicator's value should read on screen, given what kind of number it is. */
export function indicatorValue(indicator: { field: string; value: number | null }): string {
  if (indicator.value === null) return "—";
  const unit =
    indicator.field.endsWith("_margin") ? "pct"
    : indicator.field === "working_capital" || indicator.field === "total_financial_system_debt" ?
      "ARS"
    : indicator.field === "bcra_situation_max" || indicator.field === "bounced_checks_count" ?
      "count"
    : "ratio";
  return formatValue({ value: indicator.value, unit } as never);
}

export function trendRows(score: RiskScoreOut) {
  const trends = score.payload.trends?.trends ?? [];
  return trends.filter((t) => t.material);
}

/**
 * Cómo se lee una palanca: el valor requerido, no el delta.
 *
 * «Llevar la cobertura de intereses a 2,01» es accionable; «subirla 0,21» obliga al lector a hacer
 * la cuenta, que es justo lo que la herramienta viene a evitar.
 */
export function leverRows(score: RiskScoreOut) {
  const levers = score.payload.levers;
  if (!levers) return [];
  return levers.levers.map((l) => ({
    ...l,
    value: indicatorValue({ field: l.field, value: l.current }),
    target: indicatorValue({ field: l.field, value: l.required }),
  }));
}

export function leverCombination(score: RiskScoreOut) {
  const levers = score.payload.levers;
  if (!levers?.combination.length) return null;
  return {
    steps: levers.combination.map((l) => ({
      ...l,
      value: indicatorValue({ field: l.field, value: l.current }),
      target: indicatorValue({ field: l.field, value: l.required }),
    })),
    total: Math.round(levers.combination.reduce((sum, l) => sum + l.gain, 0) * 10) / 10,
    reaches: levers.next_grade,
  };
}

export function stressRows(score: RiskScoreOut) {
  const stress = score.payload.stress;
  if (!stress?.computable) return [];
  return stress.scenarios.filter((s) => s.coverage !== null);
}

// --- what the analyst decided -----------------------------------------------------------------

const DECISION_TONE: Record<string, Tone> = {
  approved: "good",
  approved_with_conditions: "warn",
  rejected: "bad",
  more_information: "neutral",
};

export function decisionChip(decision: { outcome: string } | null | undefined): Chip | null {
  if (!decision) return null;
  return {
    text: label(DECISIONS, decision.outcome),
    tone: DECISION_TONE[decision.outcome] ?? "neutral",
  };
}

export function decisionMeaning(outcome: string): string {
  return DECISION_MEANING[outcome] ?? "";
}

/**
 * Whether a decision lands where the system pointed, computed for the form as the analyst types.
 *
 * Showing it live is the point: an analyst about to disagree with the draft should see that they
 * are disagreeing, before they save, not after.
 */
export function agreesWithSystem(
  outcome: string,
  recommendation: string | null | undefined,
): boolean | null {
  if (!recommendation) return null;
  return DECISION_MATCHES[outcome] === recommendation;
}

export const DECISION_OPTIONS = Object.keys(DECISIONS);

// --- figures a person checked -------------------------------------------------------------------

export interface VerificationState {
  checked: boolean;
  agrees: boolean;
  comment: string | null;
  by: string | null;
}

/** Keyed by field and period, the way the figures table asks for them. */
export function verificationIndex(
  verifications: {
    field_name: string;
    period: string | null;
    agrees: boolean;
    comment: string | null;
    verified_by_name: string | null;
  }[],
): Map<string, VerificationState> {
  const out = new Map<string, VerificationState>();
  for (const v of verifications) {
    out.set(verificationKey(v.field_name, v.period), {
      checked: true,
      agrees: v.agrees,
      comment: v.comment,
      by: v.verified_by_name,
    });
  }
  return out;
}

export function verificationKey(field: string, period: string | null): string {
  return `${field}@${period ?? ""}`;
}

/** How much of a legajo a person actually checked by hand, and where they disagreed. */
export function verificationProgress(
  sections: FigureSection[],
  index: Map<string, VerificationState>,
) {
  let total = 0;
  let checked = 0;
  let disagreed = 0;
  for (const section of sections) {
    for (const row of section.rows) {
      if (!row.evidenceId) continue; // nothing to check against
      for (const period of section.periods) {
        if (!row.values[period] || row.values[period] === "—") continue;
        total += 1;
        const state = index.get(verificationKey(row.name, period === "Valor" ? null : period));
        if (state?.checked) checked += 1;
        if (state?.checked && !state.agrees) disagreed += 1;
      }
    }
  }
  return { total, checked, disagreed, fraction: total ? checked / total : 0 };
}

// --- the home screen ----------------------------------------------------------------------------

export interface MetricCard {
  label: string;
  value: string;
  hint?: string;
  tone?: Tone;
  filter?: QueueView;
}

/**
 * The numbers worth a person's morning, in the order they should read them.
 *
 * What is waiting on a human comes first; throughput and cost come last, because they describe the
 * machine and nobody changes their day because of them.
 */
export function metricCards(m: Metrics): MetricCard[] {
  const cards: MetricCard[] = [
    {
      label: "Pendientes de decisión",
      value: String(m.pendientes_de_decision),
      hint: "Analizados, esperando que alguien decida",
      tone: m.pendientes_de_decision ? "warn" : "neutral",
      filter: "Pendientes de decisión",
    },
    {
      label: "Interrumpidos",
      value: String(m.interrumpidos),
      hint: "El análisis se cortó y nadie lo reintentó",
      tone: m.interrumpidos ? "bad" : "neutral",
      filter: "Requieren atención",
    },
    {
      label: "Sin asignar",
      value: String(m.sin_asignar),
      hint: "Nadie los tiene a cargo",
      tone: m.sin_asignar ? "warn" : "neutral",
    },
    {
      label: "Olvidados",
      value: String(m.olvidados),
      hint: "Sin tocar hace más de una semana",
      tone: m.olvidados ? "bad" : "neutral",
    },
    {
      label: "En análisis",
      value: String(m.en_analisis),
      hint: "Corriendo ahora",
      filter: "En análisis",
    },
    {
      label: "Alertas críticas",
      value: String(m.alertas_criticas),
      tone: m.alertas_criticas ? "bad" : "neutral",
    },
  ];
  return cards;
}

/** The risk mix across the cartera, largest first, as a share of what has been analysed. */
export function riskBreakdown(m: Metrics) {
  const total = Object.values(m.por_riesgo).reduce((a, b) => a + b, 0);
  return Object.entries(m.por_riesgo)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([key, count]) => ({
      key,
      label: label(RECOMMENDATIONS, key),
      count,
      share: total ? count / total : 0,
      tone: (RECOMMENDATION_TONE[key] ?? "neutral") as Tone,
    }));
}

/**
 * The grade distribution, always in A→E order.
 *
 * Unlike the risk breakdown this one keeps the empty grades: a cartera with nobody in A is telling
 * you something, and a bar chart that quietly drops the empty bands hides exactly that.
 */
export function gradeBreakdown(m: Metrics) {
  const grades = ["A", "B", "C", "D", "E"];
  const total = grades.reduce((sum, g) => sum + (m.por_calificacion?.[g] ?? 0), 0);
  return grades.map((grade) => ({
    key: grade,
    label: grade,
    count: m.por_calificacion?.[grade] ?? 0,
    partial: m.por_calificacion_parcial?.[grade] ?? 0,
    share: total ? (m.por_calificacion?.[grade] ?? 0) / total : 0,
    tone: gradeChip({ grade, partial: false })!.tone,
  }));
}

export function formatDuration(seconds: number | null): string {
  if (seconds === null) return "—";
  if (seconds < 60) return `${Math.round(seconds)} s`;
  return `${Math.floor(seconds / 60)} min ${Math.round(seconds % 60)} s`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** "hace 3 días" — a queue is read in elapsed time, not in dates. */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 1) return "recién";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  if (days < 31) return `hace ${days} día${days === 1 ? "" : "s"}`;
  const months = Math.round(days / 30);
  return `hace ${months} mes${months === 1 ? "" : "es"}`;
}

// --- evidence -------------------------------------------------------------------------------

/** Where a claim comes from, said the way an analyst would cite it. */
export function evidenceCaption(evidence: EvidenceOut | null | undefined): string {
  if (!evidence) return "";
  if (evidence.document) {
    return `${evidence.document}${evidence.page ? `, pág. ${evidence.page}` : ""}`;
  }
  if (evidence.url) return evidence.url;
  return label(EVIDENCE_KINDS, evidence.kind, "fuente externa");
}

/** Whether this came from outside the legajo — shown differently, never mixed in. */
export function evidenceIsExternal(evidence: EvidenceOut): boolean {
  return evidence.kind === "web" || evidence.kind === "api" || !evidence.document;
}

// --- reports --------------------------------------------------------------------------------

/**
 * (what the analyst wants, what is kept behind "otros formatos"). Word is the deliverable a credit
 * file gets filed with; offering "Descargar MD" with equal weight asks an analyst to know what
 * Markdown is.
 */
export function reportDownloads(reports: ReportOut[]) {
  const primary = reports.filter((r) => r.format === PRIMARY_REPORT_FORMAT);
  const rest = reports.filter((r) => r.format !== PRIMARY_REPORT_FORMAT);
  const order = Object.keys(REPORT_FORMATS);
  rest.sort((a, b) => order.indexOf(a.format) - order.indexOf(b.format));
  return primary.length ?
      { primary, others: rest }
    : { primary: rest.slice(0, 1), others: rest.slice(1) };
}

export function reportLabel(format: string): string {
  return REPORT_FORMATS[format] ?? format.toUpperCase();
}
